import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { IconX } from '@tabler/icons-react';
import { TeeButton } from './tee-button';

export interface TeeFormModalProps {
  /**
   * Whether the modal is open or closed
   */
  isOpen: boolean;

  /**
   * Title of the modal
   */
  title: string;

  /**
   * Callback to close the modal
   */
  onClose: () => void;

  /**
   * Primary action label. When undefined or empty string, the primary button is hidden.
   */
  primaryActionLabel?: string;

  /**
   * Callback when primary action is clicked
   */
  onPrimaryAction: () => void | Promise<void>;

  /**
   * Secondary action label (default: "Cancel")
   */
  secondaryActionLabel?: string;

  /**
   * Callback when secondary action is clicked (default: closes modal)
   */
  onSecondaryAction?: () => void;

  /**
   * Whether the primary action is loading
   */
  isLoading?: boolean;

  /**
   * Whether the primary action is disabled
   */
  primaryActionDisabled?: boolean;

  /**
   * Label shown inside the primary button while isLoading is true.
   * Defaults to t('common.saving') if not provided.
   */
  isLoadingLabel?: string;

  /**
   * Modal content (children)
   */
  children: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Whether clicking the backdrop (outside the panel) closes the modal.
   * Defaults to true. Set false for data-entry drawers where an accidental
   * outside click would discard unsaved input — the user then exits only via
   * the X, Cancel, or Save actions.
   */
  dismissOnBackdropClick?: boolean;

  /**
   * Whether pressing Escape closes the modal. Defaults to true. Set false for
   * data-entry drawers (alongside dismissOnBackdropClick) so the user exits
   * only via the X, Cancel, or Save actions and can't lose input to a stray
   * keypress.
   */
  dismissOnEscape?: boolean;

  /**
   * Visual variant. 'panel' (default) is the original drawer. 'sheet' is the
   * compact mobile bottom sheet: eyebrow + 20px sentence-case title, no gold
   * rule, 85dvh cap and a 20px top radius.
   */
  variant?: 'panel' | 'sheet';

  /** Small line rendered above the title (sheet variant only). */
  eyebrow?: string;

  /** Custom footer content. Replaces the default secondary/primary buttons. */
  footer?: React.ReactNode;
}

/** Kept in step with the transition duration below. */
const EXIT_MS = 260;

export const TeeFormModal = React.forwardRef<HTMLDivElement, TeeFormModalProps>(
  (
    {
      isOpen,
      title,
      onClose,
      primaryActionLabel,
      onPrimaryAction,
      secondaryActionLabel = 'Cancel',
      onSecondaryAction,
      isLoading = false,
      isLoadingLabel,
      children,
      className,
      primaryActionDisabled = false,
      dismissOnBackdropClick = true,
      dismissOnEscape = true,
      variant = 'panel',
      eyebrow,
      footer,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const titleId = React.useId();
    const isSheet = variant === 'sheet';

    /* Enter/exit transition.
       `isOpen` alone cannot animate an exit — unmounting removes the element
       before any transition can run — so the modal stays mounted for the
       duration of the close and `mounted` is what actually gates the DOM.
       `entered` is flipped a frame after mount so the browser has an initial
       off-screen state to transition FROM; setting both in the same frame
       produces the pop this replaces. */
    const [mounted, setMounted] = React.useState(isOpen);
    const [entered, setEntered] = React.useState(false);

    React.useEffect(() => {
      if (isOpen) {
        setMounted(true);
        // Two frames: one to commit the closed state, one to start the move.
        const raf = requestAnimationFrame(() =>
          requestAnimationFrame(() => setEntered(true)),
        );
        return () => cancelAnimationFrame(raf);
      }

      setEntered(false);
      const timer = setTimeout(() => setMounted(false), EXIT_MS);
      return () => clearTimeout(timer);
    }, [isOpen]);

    /* Freeze the page behind the drawer.
       Padding compensates for the scrollbar the lock removes; without it the
       whole layout shifts sideways the instant the drawer opens, which reads
       as a jolt even though the drawer itself moved correctly. */
    React.useEffect(() => {
      if (!isOpen) return;

      const { body } = document;
      const previousOverflow = body.style.overflow;
      const previousPadding = body.style.paddingRight;
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;

      body.style.overflow = 'hidden';
      if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

      return () => {
        body.style.overflow = previousOverflow;
        body.style.paddingRight = previousPadding;
      };
    }, [isOpen]);

    // Escape closes, like every other dismissible surface in the app — unless
    // the caller opts out (data-entry drawers, so input can't be lost to a
    // stray keypress).
    React.useEffect(() => {
      if (!isOpen || !dismissOnEscape) return;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose, dismissOnEscape]);

    if (!mounted) return null;

    const handleSecondaryAction = () => {
      if (onSecondaryAction) {
        onSecondaryAction();
      } else {
        onClose();
      }
    };

    /* Rendered into <body>. `position: fixed` resolves against the nearest
       ancestor with a transform, filter or contain — not the viewport — and
       this modal is opened from deep inside page content (an expanded table
       row, a tab panel). A portal makes the geometry independent of wherever
       it happens to be called from. */
    return createPortal(
      <>
        {/* Backdrop — soft green-tinted scrim (never a flat gray wash) */}
        <div
          className="fixed inset-0 z-40 motion-safe:transition-opacity motion-safe:duration-200 motion-safe:ease-out"
          style={{
            background: isSheet ? 'rgba(16, 38, 30, 0.45)' : 'rgba(12, 61, 45, 0.30)',
            backdropFilter: 'blur(3px)',
            opacity: entered ? 1 : 0,
          }}
          onClick={dismissOnBackdropClick ? onClose : undefined}
        />

        {/* Modal container.
            Mobile: a bottom drawer — full width, anchored to the bottom edge,
            capped at 90vh so the backdrop stays tappable above it.
            `md` and up: the original right-hand side panel, unchanged.
            Both are the same fixed-position element with different edges, so
            this needs no viewport JS. */}
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={cn(
            'fixed z-50 flex flex-col bg-[var(--background-page-content)] overflow-hidden',
            // Mobile — bottom drawer
            'inset-x-0 bottom-0 max-h-[90vh] rounded-t-[16px] border-t border-[var(--border-default)]',
            // Desktop — right-hand side panel (original geometry)
            // `w-` matters as much as `max-w-`: `translate-x-full` moves the
            // element by its OWN width, so a content-sized panel slides a
            // distance that changes while it loads — the jump this replaces.
            'md:inset-x-auto md:right-0 md:top-[62px] md:bottom-0 md:max-h-[calc(100vh-62px)] md:w-[748px] md:max-w-[90vw]',
            'md:rounded-t-none md:rounded-tl-[10px] md:border-t-0 md:border-l',
            // Slides from the edge it is anchored to: up from the bottom on
            // mobile, in from the right on desktop. `motion-safe` so a reduced
            // -motion preference gets the fade alone.
            'motion-safe:transition-transform motion-safe:duration-[260ms] motion-safe:ease-[cubic-bezier(0.32,0.72,0,1)]',
            entered
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-full',
            isSheet && 'max-h-[85dvh] rounded-t-[20px]',
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Grab handle — mobile affordance that this sheet is dismissible */}
          <div className="md:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
            <span aria-hidden="true" className="h-1 w-10 rounded-full bg-[var(--border-default)]" />
          </div>

          {/* Header Section */}
          <div className="flex items-start justify-between gap-6 px-6 pt-4 pb-3 flex-shrink-0 md:pt-6">
            <div className="flex-1 flex flex-col gap-2 min-w-0">
              {isSheet ? (
                <>
                  {eyebrow && (
                    <p className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
                      {eyebrow}
                    </p>
                  )}
                  <h2
                    id={titleId}
                    className="text-[20px] font-semibold leading-[1.2] font-[family-name:var(--type-font-family-heading)] text-[var(--text-content-title)]"
                  >
                    {title}
                  </h2>
                </>
              ) : (
                <>
                  <h1
                    id={titleId}
                    className="text-heading-h3 md:text-heading-h1 font-extrabold uppercase tracking-wider font-[family-name:var(--type-font-family-heading)] text-[var(--text-content-title)]"
                  >
                    {title}
                  </h1>
                  {/* Gold rule beneath the sheet title */}
                  <span aria-hidden="true" className="h-[3px] w-16 bg-[var(--color-gold-500)] rounded-full" />
                </>
              )}
            </div>
            <TeeButton
              variant="ghost"
              size="s"
              onClick={onClose}
              aria-label="Close modal"
              leftIcon={<IconX size={24} className="text-[var(--text-content-title)]" />}
              className={cn('flex-shrink-0', isSheet && 'min-h-[44px] min-w-[44px]')}
            />
          </div>

          {/* Body Section */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 md:py-6">{children}</div>

          {/* Footer Section. CTAs sit side by side at every width, splitting the
              row evenly on mobile — matching TeeSectionFooter — and padded for
              the home indicator. */}
          <div className={cn('flex flex-row items-center gap-3 px-6 py-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] border-t border-[var(--border-default)] flex-shrink-0 bg-[var(--background-page-content)] shadow-[0px_0px_15px_0px_rgba(0,0,0,0.4)] md:justify-end md:gap-6 md:py-6 md:pb-6', isSheet && 'pb-[clamp(16px,env(safe-area-inset-bottom,0px),24px)]')}>
            {footer ?? (<>
            {secondaryActionLabel && (
              <TeeButton
                variant="secondary"
                size="s"
                onClick={handleSecondaryAction}
                disabled={isLoading}
                className="flex-1 md:flex-none md:w-auto md:min-w-[150px]"
              >
                {secondaryActionLabel}
              </TeeButton>
            )}
            {primaryActionLabel && (
              <TeeButton
                variant="primary"
                size="s"
                onClick={onPrimaryAction}
                disabled={isLoading || primaryActionDisabled}
                className="flex-1 md:flex-none md:w-auto md:min-w-[150px]"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="inline-block w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {isLoadingLabel ?? t('common.saving')}
                  </span>
                ) : primaryActionLabel}
              </TeeButton>
            )}
            </>)}
          </div>
        </div>
      </>,
      document.body,
    );
  }
);

TeeFormModal.displayName = 'TeeFormModal';

export { TeeFormModal as default };
